import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  LogOut, 
  Edit2, 
  CheckCircle, 
  Clock, 
  XCircle,
  Shield,
  ExternalLink,
  FileText,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Zap,
  Eye,
  BarChart3,
  Lock,
  Unlock,
  Info,
  Leaf,
  Link2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { VendorSustainabilityModule } from '@/components/vendor/VendorSustainabilityModule';
import { VendorAnalytics } from '@/components/vendor/VendorAnalytics';
import { PlatformSelector, getInitialPlatformId } from '@/components/vendor/PlatformSelector';
import { PlatformLinkRequestForm, PlatformLinkRequestHistory } from '@/components/vendor/PlatformLinkRequest';
import { vendorApi } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { SEO } from '@/components/SEO';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const TIER_COLORS = {
  claimed: 'bg-muted text-muted-foreground border-muted-foreground/20',
  verified: 'bg-blue-100 text-blue-700 border-blue-300',
  enterprise: 'bg-amber-100 text-amber-700 border-amber-300',
};

const EDIT_STATUS = {
  pending: { label: 'Pending Review', icon: Clock, className: 'text-amber-600' },
  approved: { label: 'Approved', icon: CheckCircle, className: 'text-success' },
  rejected: { label: 'Rejected', icon: XCircle, className: 'text-destructive' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'Never';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function VendorDashboardPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [vendor, setVendor] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [edits, setEdits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlatformId, setSelectedPlatformId] = useState(null);
  const [showLinkRequestForm, setShowLinkRequestForm] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [vendorData, platformsData, editsData] = await Promise.all([
        vendorApi.getMe(),
        vendorApi.getPlatforms(),
        vendorApi.getEdits()
      ]);
      setVendor(vendorData);
      setPlatforms(platformsData);
      setEdits(editsData);
      
      // Set initial selected platform
      if (platformsData.length > 0 && !selectedPlatformId) {
        const initialId = getInitialPlatformId(platformsData, vendorData.primary_platform_id);
        setSelectedPlatformId(initialId);
      }
    } catch (err) {
      // Handle authentication errors - clear token and redirect to login
      if (err.message.includes('401') || err.message.includes('authenticated') || err.message.includes('Unauthorized')) {
        localStorage.removeItem('vendor_token');
        localStorage.removeItem('vendor_info');
        toast({ title: 'Session expired', description: 'Please log in again', variant: 'destructive' });
        navigate('/vendor');
        return;
      } else {
        toast({ title: 'Error', description: err.message, variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, toast, selectedPlatformId]);

  useEffect(() => {
    const vendorToken = localStorage.getItem('vendor_token');
    if (!vendorToken) {
      navigate('/vendor');
      return;
    }
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    try {
      await vendorApi.logout();
    } catch (err) {
      // Ignore logout errors
    }
    localStorage.removeItem('vendor_token');
    localStorage.removeItem('vendor_info');
    navigate('/vendor');
  };

  // Check if vendor can manage multiple platforms (Verified/Enterprise)
  const canManageMultiple = vendor?.permissions?.can_manage_multiple_platforms || 
    ['verified', 'enterprise'].includes(vendor?.tier?.toLowerCase());

  // Handle platform change
  const handlePlatformChange = (platformId) => {
    setSelectedPlatformId(platformId);
  };

  // Handle link request success
  const handleLinkRequestSuccess = () => {
    setShowLinkRequestForm(false);
    fetchData(); // Refresh data
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!vendor) {
    return null;
  }

  const tierColor = TIER_COLORS[vendor.tier] || TIER_COLORS.claimed;
  const pendingEdits = edits.filter(e => e.status === 'pending').length;
  const approvedEdits = edits.filter(e => e.status === 'approved').length;
  const permissions = vendor.permissions || {};
  const tierInfo = vendor.tier_info || { name: 'Claimed', badge_text: 'Claimed' };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO 
        title="Vendor Dashboard | GoCompareAI" 
        description="Manage your AI platform profile on GoCompareAI"
        noindex={true}
      />
      <Header />
      
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold">{vendor.company_name}</h1>
              <p className="text-muted-foreground">Vendor Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Platform Selector (for Verified/Enterprise with multiple platforms) */}
            {canManageMultiple && platforms.length > 0 && (
              <PlatformSelector
                platforms={platforms}
                selectedPlatformId={selectedPlatformId}
                onPlatformChange={handlePlatformChange}
                primaryPlatformId={vendor.primary_platform_id}
                canRequestMore={canManageMultiple}
                onRequestPlatform={() => setShowLinkRequestForm(true)}
              />
            )}
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Platform Link Request Form (for Verified/Enterprise) */}
        {canManageMultiple && (
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {platforms.length === 1 && (
                <p className="text-sm text-muted-foreground">
                  Managing: <span className="font-medium">{platforms[0]?.name}</span>
                </p>
              )}
            </div>
            <PlatformLinkRequestForm 
              onSuccess={handleLinkRequestSuccess}
              linkedPlatformIds={vendor.linked_platform_ids || []}
            />
          </div>
        )}

        {/* Tier & Permissions Card */}
        <Card className="mb-8 border-2 border-primary/10">
          <CardContent className="pt-6">
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              {/* Tier Badge */}
              <div className="flex items-center gap-3">
                <div className={cn("px-4 py-2 rounded-lg border font-medium", tierColor)}>
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    {tierInfo.badge_text || tierInfo.name}
                  </div>
                </div>
              </div>
              
              {/* Permissions Summary */}
              <div className="flex flex-wrap gap-4 text-sm">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1.5">
                        {permissions.approval_required ? (
                          <Clock className="w-4 h-4 text-amber-500" />
                        ) : (
                          <Zap className="w-4 h-4 text-success" />
                        )}
                        <span className="text-muted-foreground">
                          {permissions.approval_required ? 'Approval Required' : 'Fast Lane Publishing'}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      {permissions.approval_required 
                        ? 'Your edits require admin review before going live'
                        : 'Safe field edits publish instantly without approval'}
                    </TooltipContent>
                  </Tooltip>
                  
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1.5">
                        <Edit2 className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">
                          {permissions.max_edits_per_day} edits/day
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      Daily edit limit for your tier
                    </TooltipContent>
                  </Tooltip>
                  
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1.5">
                        {permissions.can_view_analytics ? (
                          <BarChart3 className="w-4 h-4 text-success" />
                        ) : (
                          <Lock className="w-4 h-4 text-muted-foreground" />
                        )}
                        <span className={permissions.can_view_analytics ? 'text-foreground' : 'text-muted-foreground'}>
                          Analytics
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      {permissions.can_view_analytics 
                        ? 'You can view platform analytics'
                        : 'Upgrade to Verified tier to view analytics'}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
            
            {/* Auto-publish fields info for Enterprise */}
            {permissions.auto_publish_fields?.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-start gap-2">
                  <Zap className="w-4 h-4 text-success mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-success">Enterprise Fast Lane Active</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      These fields publish instantly: {permissions.auto_publish_fields.join(', ')}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Platforms</p>
                  <p className="text-xl font-semibold">{platforms.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Last Login</p>
                  <p className="text-sm font-medium">{formatDate(vendor.last_login)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Last Profile Update</p>
                  <p className="text-sm font-medium">{formatDate(vendor.last_profile_update)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="platforms" className="space-y-6">
          <TabsList>
            <TabsTrigger value="platforms" className="gap-2">
              <Building2 className="w-4 h-4" />
              My Platforms
            </TabsTrigger>
            <TabsTrigger value="analytics" className="gap-2" data-testid="analytics-tab">
              <BarChart3 className="w-4 h-4" />
              Analytics
              {!permissions.can_view_analytics && (
                <Lock className="w-3 h-3 ml-1 text-muted-foreground" />
              )}
            </TabsTrigger>
            <TabsTrigger value="sustainability" className="gap-2">
              <Leaf className="w-4 h-4" />
              Sustainability
            </TabsTrigger>
            <TabsTrigger value="edits" className="gap-2">
              <FileText className="w-4 h-4" />
              My Edits
              {pendingEdits > 0 && (
                <Badge variant="secondary" className="ml-1">{pendingEdits}</Badge>
              )}
            </TabsTrigger>
            {canManageMultiple && (
              <TabsTrigger value="requests" className="gap-2" data-testid="requests-tab">
                <Link2 className="w-4 h-4" />
                Link Requests
              </TabsTrigger>
            )}
          </TabsList>

          {/* Platforms Tab */}
          <TabsContent value="platforms" className="space-y-4">
            {/* Important Notice */}
            <Card className="bg-muted/50 border-border/50">
              <CardContent className="flex items-start gap-3 py-4">
                <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">What you can edit</p>
                  <p>
                    You can update factual profile information such as descriptions, features, pricing, 
                    and data handling details. All edits are reviewed before publishing.
                  </p>
                  <p className="mt-2 text-xs">
                    <strong>Note:</strong> &quot;Our assessment&quot; sections, confidence indicators, and suitability 
                    logic are managed independently by GoCompareAI and cannot be edited.
                  </p>
                </div>
              </CardContent>
            </Card>

            {platforms.length === 0 ? (
              <Card className="py-12">
                <CardContent className="text-center">
                  <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium mb-2">No Linked Platforms</p>
                  <p className="text-muted-foreground">
                    You don&apos;t have any platforms linked to your account yet.
                    Contact GoCompareAI to link your platform profile.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {platforms.map(platform => (
                  <Card key={platform.id}>
                    <CardContent className="py-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-lg">{platform.name}</h3>
                            {platform.verification_status === 'verified' && (
                              <Badge className="bg-primary/10 text-primary">Verified</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{platform.provider}</p>
                          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                            {platform.summary}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 sm:flex-shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(`/platform/${platform.id}`, '_blank')}
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            View Public
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => navigate(`/vendor/edit/${platform.id}`)}
                          >
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit Profile
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-4">
            <VendorAnalytics 
              vendor={vendor} 
              platforms={platforms}
              selectedPlatformId={selectedPlatformId}
            />
          </TabsContent>

          {/* Platform Link Requests Tab (for Verified/Enterprise) */}
          {canManageMultiple && (
            <TabsContent value="requests" className="space-y-4">
              <PlatformLinkRequestHistory />
            </TabsContent>
          )}

          {/* Sustainability Tab */}
          <TabsContent value="sustainability" className="space-y-4">
            {platforms.length > 0 ? (
              <div className="space-y-6">
                {platforms.map(platform => (
                  <VendorSustainabilityModule
                    key={platform.id}
                    platformId={platform.id}
                    token={localStorage.getItem('vendor_token')}
                  />
                ))}
              </div>
            ) : (
              <Card className="py-12">
                <CardContent className="text-center">
                  <Leaf className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium mb-2">No Platforms Linked</p>
                  <p className="text-muted-foreground">
                    You need at least one linked platform to manage sustainability information.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Edits Tab */}
          <TabsContent value="edits" className="space-y-4">
            {edits.length === 0 ? (
              <Card className="py-12">
                <CardContent className="text-center">
                  <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium mb-2">No Edit History</p>
                  <p className="text-muted-foreground">
                    You haven&apos;t submitted any profile edits yet.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {edits.map(edit => {
                  const status = EDIT_STATUS[edit.status] || EDIT_STATUS.pending;
                  const StatusIcon = status.icon;
                  const changedFields = Object.keys(edit.field_changes || {});
                  
                  return (
                    <Card key={edit.id}>
                      <CardContent className="py-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-medium">{edit.platform_name}</h3>
                              <div className={cn('flex items-center gap-1 text-sm', status.className)}>
                                <StatusIcon className="w-4 h-4" />
                                {status.label}
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              Submitted {formatDate(edit.created_at)}
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">
                              Fields changed: {changedFields.join(', ')}
                            </p>
                            {edit.admin_review_notes && (
                              <div className="mt-2 p-2 bg-muted rounded text-sm">
                                <p className="font-medium text-xs text-muted-foreground mb-1">Admin notes:</p>
                                <p>{edit.admin_review_notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
      
      <Footer />
    </div>
  );
}
